import { useState, useRef, useEffect } from 'react';
import { generateSessionId } from '../../../services/chatApi';
import { Message, ContactFormData, FormField, validateEmail, submitContactForm, sendChatMessage } from '../utils/chatUtils';
import { chatAnalytics } from '../../../utils/chatAnalytics';

interface UseChatWidgetProps {
  initialMessage: string;
}

// Chat widget analytics constants
const CHAT_EVENTS = {
  OPEN: 'Open',
  CLOSE: 'Close',
  SEND_MESSAGE: 'SendMessage',
  RECEIVE_MESSAGE: 'ReceiveMessage',
  START_FORM: 'StartContactForm',
  CANCEL_FORM: 'CancelContactForm',
  SUBMIT_FORM: 'SubmitContactForm',
  ERROR: 'Error'
};

interface BotConfig {
  botName: string;
  botSubtitle: string;
}

// Use environment variable with relative path fallback
// Ensure the API URL is trimmed to avoid issues with trailing spaces
const API_URL = (
  typeof window !== 'undefined' && 
  (window as Window & { ENV?: { VITE_API_URL?: string } }).ENV?.VITE_API_URL || 
  import.meta.env.VITE_API_URL || 
  '/api'
).trim();

// Helper function for local session ID generation (fallback)
const generateLocalSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const useChatWidget = ({ initialMessage }: UseChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: initialMessage,
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  const [isContactFormMode, setIsContactFormMode] = useState(false);
  const [contactFormStep, setContactFormStep] = useState<FormField | 'confirmation' | null>(null);
  const [contactFormData, setContactFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Track chat session duration
  const [chatStartTime, setChatStartTime] = useState<number | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [botConfig, setBotConfig] = useState<BotConfig>({
    botName: 'Assistente Moa',
    botSubtitle: '24h online'
  });
  
  // Use a local session ID initially, then update it when the API call completes
  const [sessionId, setSessionId] = useState(generateLocalSessionId());
  
  // Initialize session ID from API
  useEffect(() => {
    const initSessionId = async () => {
      try {
        const apiSessionId = await generateSessionId();
        setSessionId(apiSessionId);
      } catch (error) {
        console.error('Failed to get session ID from API, using local fallback');
        // Keep using the local session ID
      }
    };
    
    initSessionId();
  }, []);
  
  // Detect if the device is mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  useEffect(() => {
    const fetchBotConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/bot-config`);
        if (response.ok) {
          const config = await response.json();
          setBotConfig(config);
        }
      } catch (error) {
        console.error('Error fetching bot configuration:', error);
      }
    };
    
    fetchBotConfig();
  }, []);
  
  // Track chat session duration when chat is opened/closed
  useEffect(() => {
    if (isOpen && !chatStartTime) {
      setChatStartTime(Date.now());
      // Track chat open event
      chatAnalytics.trackChatOpen(sessionId, isMobile);
    } else if (!isOpen && chatStartTime) {
      const duration = Math.floor((Date.now() - chatStartTime) / 1000); // Duration in seconds
      // Track chat close event with duration
      chatAnalytics.trackChatClose(sessionId, isMobile, messages.length - 1, duration);
      setChatStartTime(null);
    }
  }, [isOpen, chatStartTime, isMobile, sessionId, messages.length]);
  
  // Disable body scrolling when chat is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden'; // Also prevent html element scrolling
      document.documentElement.style.position = 'fixed'; // Prevent bounce effect on iOS
      document.documentElement.style.width = '100%';
      document.documentElement.style.height = '100%';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
    };
  }, [isMobile, isOpen]);

  // Force layout recalculation when chat opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Force a reflow
        if (messagesEndRef.current) {
          const container = messagesEndRef.current.closest('.chat-messages');
          if (container instanceof HTMLElement) {
            // Force scroll to bottom
            container.scrollTop = container.scrollHeight;
            
            // Try again after a slightly longer delay (helps with dynamic content)
            setTimeout(() => {
              container.scrollTop = container.scrollHeight;
            }, 300);
          }
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Also ensure scrolling when messages change
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Immediate scroll
      const scrollToBottom = () => {
        if (messagesEndRef.current) {
          const container = messagesEndRef.current.closest('.chat-messages');
          if (container instanceof HTMLElement) {
            // Check if user is already at the bottom before scrolling
            const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
            
            // Only auto-scroll if user is already at the bottom or this is a new message
            if (isAtBottom) {
              container.scrollTop = container.scrollHeight;
            }
          }
        }
      };
      
      // Scroll immediately
      scrollToBottom();
      
      // And then set up multiple scroll attempts with increasing delays
      // This helps with long messages that take time to render
      const scrollTimers = [50, 300, 800, 1500].map(delay => 
        setTimeout(scrollToBottom, delay)
      );
      
      return () => scrollTimers.forEach(timer => clearTimeout(timer));
    }
  }, [messages, isOpen]);
  
  // Mark all but the latest bot message as already animated when opening the chat
  useEffect(() => {
    // Only run this logic when the chat is opened
    if (!isOpen) return;
    
    try {
      // Get all message IDs except the latest bot message
      const botMessages = messages.filter(msg => msg.sender === 'bot');
      if (botMessages.length <= 1) return; // No need to do anything if there's only one bot message
      
      const latestBotMessageId = botMessages[botMessages.length - 1].id;
      const previousBotMessageIds = botMessages
        .slice(0, botMessages.length - 1)
        .map(msg => msg.id);
      
      // Get current animated messages from localStorage
      const savedAnimated = localStorage.getItem('chat_animated_messages');
      const animatedMessages = savedAnimated ? JSON.parse(savedAnimated) : {};
      
      // Mark all previous bot messages as animated
      previousBotMessageIds.forEach(id => {
        animatedMessages[id] = true;
      });
      
      // Save back to localStorage
      localStorage.setItem('chat_animated_messages', JSON.stringify(animatedMessages));
    } catch (e) {
      console.error('Error updating animated messages in localStorage:', e);
    }
  }, [isOpen, messages]);
  
  // Additional scroll check for typing state
  useEffect(() => {
    if (isOpen && isTyping) {
      const interval = setInterval(() => {
        if (messagesEndRef.current) {
          const container = messagesEndRef.current.closest('.chat-messages');
          if (container instanceof HTMLElement) {
            // Check if user is already at the bottom before scrolling
            const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
            
            // Only auto-scroll if user is already at the bottom
            if (isAtBottom) {
              container.scrollTop = container.scrollHeight;
            }
          }
        }
      }, 500);
      
      return () => clearInterval(interval);
    }
  }, [isTyping, isOpen]);

  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    // If opening the chat, mark that we've opened it before
    if (newIsOpen) {
      try {
        localStorage.setItem('chat_has_been_opened', 'true');
      } catch (e) {
        console.error('Error saving chat open state to localStorage:', e);
      }
    }
  };

  const addMessageToChat = (message: Message) => {
    setMessages(prev => [...prev, message]);
    
    // Track message events for analytics
    if (message.sender === 'user') {
      chatAnalytics.trackUserMessage(
        sessionId, 
        message.text.length, 
        isContactFormMode, 
        contactFormStep
      );
    } else {
      chatAnalytics.trackBotMessage(
        sessionId, 
        message.text.length, 
        isContactFormMode, 
        contactFormStep
      );
    }
  };

  const startContactForm = () => {
    // Check if already in contact form mode to prevent restarting
    if (isContactFormMode) {
      return; // Exit early if already in contact form mode
    }
    
    setIsContactFormMode(true);
    setContactFormStep('name');
    
    // Track contact form start
    chatAnalytics.trackContactFormStart(sessionId, isMobile);
    
    const botMessage: Message = {
      id: Date.now().toString(),
      text: 'Você solicitou o preenchimento de um formulário de contato. Vou guiá-lo pelo processo.\n\nPor favor, digite seu nome completo:',
      sender: 'bot',
      timestamp: new Date()
    };
    
    addMessageToChat(botMessage);
  };

  const exitContactForm = () => {
    // Track contact form cancellation
    chatAnalytics.trackContactFormCancel(
      sessionId,
      contactFormStep,
      Object.values(contactFormData).filter(Boolean).length
    );
    
    setIsContactFormMode(false);
    setContactFormStep(null);
    setContactFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    
    const botMessage: Message = {
      id: Date.now().toString(),
      text: 'Formulário de contato cancelado. Como posso ajudar você?',
      sender: 'bot',
      timestamp: new Date()
    };
    
    addMessageToChat(botMessage);
  };

  const processContactFormInput = (input: string) => {
    if (!contactFormStep) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date()
    };
    
    addMessageToChat(userMessage);
    
    switch (contactFormStep) {
      case 'name':
        setContactFormData(prev => ({ ...prev, name: input }));
        setContactFormStep('email');
        
        // Track form field completion
        chatAnalytics.trackFormFieldComplete(sessionId, 'name', input.length);
        
        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: 'Obrigado! Agora, por favor, digite seu email:',
            sender: 'bot',
            timestamp: new Date()
          };
          
          addMessageToChat(botMessage);
        }, 500);
        break;
        
      case 'email':
        if (!validateEmail(input)) {
          // Track validation error
          chatAnalytics.trackValidationError(sessionId, 'email');
          
          setTimeout(() => {
            const botMessage: Message = {
              id: Date.now().toString(),
              text: 'O email informado parece inválido. Por favor, digite um email válido:',
              sender: 'bot',
              timestamp: new Date()
            };
            
            addMessageToChat(botMessage);
          }, 500);
          return;
        }
        
        setContactFormData(prev => ({ ...prev, email: input }));
        setContactFormStep('subject');
        
        // Track form field completion
        chatAnalytics.trackFormFieldComplete(sessionId, 'email');
        
        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: 'Qual o assunto da sua mensagem?',
            sender: 'bot',
            timestamp: new Date()
          };
          
          addMessageToChat(botMessage);
        }, 500);
        break;
        
      case 'subject':
        setContactFormData(prev => ({ ...prev, subject: input }));
        setContactFormStep('message');
        
        // Track form field completion
        chatAnalytics.trackFormFieldComplete(sessionId, 'subject', input.length);
        
        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: 'Por último, digite sua mensagem:',
            sender: 'bot',
            timestamp: new Date()
          };
          
          addMessageToChat(botMessage);
        }, 500);
        break;
        
      case 'message':
        setContactFormData(prev => ({ ...prev, message: input }));
        
        // Track form field completion
        chatAnalytics.trackFormFieldComplete(sessionId, 'message', input.length);
        
        setTimeout(() => {
          const summary = `
Nome: ${contactFormData.name}
Email: ${contactFormData.email}
Assunto: ${contactFormData.subject}
Mensagem: ${input}

Está tudo correto? Digite "sim" para enviar ou "não" para cancelar.`;
          
          const botMessage: Message = {
            id: Date.now().toString(),
            text: summary,
            sender: 'bot',
            timestamp: new Date()
          };
          
          addMessageToChat(botMessage);
          setContactFormStep('confirmation');
        }, 500);
        break;
        
      case 'confirmation':
        if (input.toLowerCase() === 'sim') {
          handleSubmitContactForm();
        } else {
          exitContactForm();
        }
        break;
    }
    
    setCurrentMessage('');
  };

  const handleSubmitContactForm = async () => {
    setIsSubmitting(true);
    
    // Track form submission attempt
    chatAnalytics.trackContactFormSubmit(sessionId, contactFormData);
    
    try {
      await submitContactForm(
        contactFormData,
        sessionId,
        (message) => {
          addMessageToChat(message);
          setIsContactFormMode(false);
          setContactFormStep(null);
          setContactFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
        },
        (message) => {
          addMessageToChat(message);
          
          // Track submission error
          chatAnalytics.trackChatError(
            sessionId,
            'FormSubmitError',
            'Contact form submission failed'
          );
        }
      );
    } catch (error) {
      // Track error
      chatAnalytics.trackChatError(
        sessionId,
        'FormSubmissionError',
        error instanceof Error ? error.message : 'Unknown error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (currentMessage.trim() === '') return;
    
    if (isContactFormMode && contactFormStep) {
      processContactFormInput(currentMessage);
      return;
    }
    
    const lowerMessage = currentMessage.toLowerCase().trim();
    
    if (lowerMessage === '/ajuda' || lowerMessage === '/help' || lowerMessage === 'ajuda' || lowerMessage === 'help') {
      const userMessage: Message = {
        id: Date.now().toString(),
        text: currentMessage,
        sender: 'user',
        timestamp: new Date()
      };
      
      addMessageToChat(userMessage);
      
      setCurrentMessage('');
      
      const helpMessage: Message = {
        id: Date.now().toString(),
        text: `Comandos disponíveis:\n\n/contato ou formulário - Inicia o formulário de contato\n/ajuda ou help - Mostra esta mensagem de ajuda\n/mais ou more - Solicita mais detalhes sobre a última resposta\n\nSe preferir falar diretamente com nossa equipe, clique no ícone de envelope acima para enviar uma mensagem. Estou aqui para ajudar com informações do site!`,
        sender: 'bot',
        timestamp: new Date()
      };
      
      addMessageToChat(helpMessage);
      
      // Track help command usage
      chatAnalytics.trackCommandUsed(sessionId, '/help');
      
      return;
    }

    if (lowerMessage === '/mais' || lowerMessage === '/more' || lowerMessage === 'mais' || lowerMessage === 'more') {
      const userMessage: Message = {
        id: Date.now().toString(),
        text: currentMessage,
        sender: 'user',
        timestamp: new Date()
      };
      
      addMessageToChat(userMessage);
      setCurrentMessage('');
      
      setIsTyping(true);
      
      // Track more command usage
      chatAnalytics.trackCommandUsed(sessionId, '/more');
      
      try {
        await sendChatMessage(
          currentMessage,
          sessionId,
          API_URL,
          true,
          (message) => {
            setIsTyping(false);
            addMessageToChat(message);
          },
          (message) => {
            setIsTyping(false);
            addMessageToChat(message);
          }
        );
      } catch (err) {
        console.error('Erro ao solicitar mais detalhes:', err);
        setIsTyping(false);
        
        // Track error
        chatAnalytics.trackChatError(
          sessionId,
          'RequestMoreDetailsError',
          err instanceof Error ? err.message : 'Unknown error'
        );
      }
      
      return;
    }
    
    const formCommands = [
      'formulario', 'formulário', 
      'contato', 'contacto',
      'enviar mensagem', 'enviar email',
      'quero enviar uma mensagem', 'quero enviar um email',
      'preciso enviar uma mensagem', 'preciso enviar um email',
      'falar com alguem', 'falar com alguém',
      'quero falar com alguem', 'quero falar com alguém',
      '/contato', '/formulario', '/form'
    ];
    
    const isFormRequest = formCommands.some(cmd => 
      lowerMessage === cmd || lowerMessage.startsWith(cmd + ' ')
    );
    
    if (isFormRequest) {
      const userMessage: Message = {
        id: Date.now().toString(),
        text: currentMessage,
        sender: 'user',
        timestamp: new Date()
      };
      
      addMessageToChat(userMessage);
      setCurrentMessage('');
      
      // Track contact form command
      chatAnalytics.trackCommandUsed(sessionId, 'contact_form', currentMessage);
      
      setTimeout(() => {
        startContactForm();
      }, 500);
      
      return;
    }
    
    const userMessage: Message = {
      id: Date.now().toString(),
      text: currentMessage,
      sender: 'user',
      timestamp: new Date()
    };
    
    addMessageToChat(userMessage);
    
    setCurrentMessage('');
    setIsTyping(true);
    
    try {
      await sendChatMessage(
        currentMessage,
        sessionId,
        API_URL,
        false,
        (message) => {
          setIsTyping(false);
          addMessageToChat(message);
        },
        (message) => {
          setIsTyping(false);
          addMessageToChat(message);
        }
      );
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setIsTyping(false);
      
      // Track error
      chatAnalytics.trackChatError(
        sessionId,
        'SendMessageError',
        err instanceof Error ? err.message : 'Unknown error'
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return {
    isOpen,
    messages,
    currentMessage,
    isTyping,
    isContactFormMode,
    contactFormStep,
    isSubmitting,
    messagesEndRef,
    botConfig,
    isMobile,
    toggleChat,
    startContactForm,
    exitContactForm,
    setCurrentMessage,
    handleSendMessage,
    handleKeyPress
  };
};
